<template>
    <Drawer
        :visible="open"
        position="right"
        modal
        dismissable
        :show-close-icon="false"
        :class="['alert-drawer', $isMobile ? 'tw:w-full' : 'tw:w-[440px]']"
        :pt="drawerPt"
        data-test="alert-drawer"
        @show="focusCloseButton"
        @update:visible="value => !value && closeAlertDrawer()"
    >
        <template #header>
            <div class="tw:flex tw:w-full tw:items-center tw:gap-2.5">
                <h2 id="alert-drawer-title" class="tw:m-0 tw:text-xl tw:font-semibold tw:text-color">Notifications</h2>
                <Badge v-if="notificationsCounter > 0" :value="notificationsCounter" data-test="alert-drawer-unread-count" />
                <span class="tw:flex-1" />
                <PrimeVueButton
                    label="Mark all as read"
                    size="small"
                    text
                    :disabled="!hasUnread"
                    data-test="alert-drawer-mark-all"
                    @click="markAllAsRead"
                />
                <PrimeVueButton
                    ref="closeButton"
                    icon="pi pi-times"
                    aria-label="Close"
                    severity="secondary"
                    text
                    rounded
                    data-test="alert-drawer-close"
                    @click="closeAlertDrawer()"
                />
            </div>
        </template>

        <div v-if="!isLoaded">
            <AlertCard
                v-for="notification in notifications"
                :key="notification.notification_id"
                :notification="notification"
                @update-notification="updateNotification"
            />
            <InfiniteLoading
                v-if="hasNextPage && !infiniteIsLoading"
                spinner="spiral"
                @distance="4"
                @infinite="handleLoadMore"
            />
        </div>
        <div
            v-if="notifications.length === 0"
            class="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:px-6 tw:py-12 tw:text-center tw:text-muted-color"
            data-test="alert-drawer-empty"
        >
            <i class="pi pi-inbox tw:text-3xl" aria-hidden="true" />
            <p class="tw:m-0 tw:text-base tw:font-semibold tw:text-color">You're all caught up</p>
        </div>

        <template v-if="canOpenInbox" #footer>
            <PrimeVueButton
                as="router-link"
                :to="{ name: 'inbox' }"
                label="View all in Inbox"
                icon="pi pi-arrow-right"
                icon-pos="right"
                text
                data-test="alert-drawer-view-inbox"
                @click="closeAlertDrawer()"
            />
        </template>
    </Drawer>
</template>

<script>
import Badge from 'primevue/badge';
import PrimeVueButton from 'primevue/button';
import Drawer from 'primevue/drawer';
import InfiniteLoading from 'vue-infinite-loading';
import { mapState } from 'vuex';

import AlertCard from '@/components/alerts/AlertCard.vue';
import { handleResponseError } from '@/Helpers.js';
import BellNotification from '@/models/BellNotification.js';
import User from '@/models/User.js';
import Gate from '@/plugins/vue-gate/Gate.js';
import { canViewInbox } from '@/policies/InboxPolicy.js';

const loadMoreRetryDelay = 2000;

const drawerPt = {
    root: { 'aria-labelledby': 'alert-drawer-title' },
    header: { class: 'tw:gap-2 tw:border-b tw:border-surface tw:px-[1.1rem] tw:py-3' },
    content: { class: 'tw:p-0' },
    footer: { class: 'tw:flex tw:justify-center tw:border-t tw:border-surface tw:p-2' },
};

// Keep the undo control while open, then remove only confirmed reads on close.
// Cleanup also runs while unrelated legacy writes or refresh requests are pending.
async function removeReadReminders(updates, hasNewerUpdate) {
    const completed = await Promise.all(
        updates.map(async update => ({
            ...update,
            succeeded: await update.pending,
        }))
    );
    const latest = new Map(completed.map(update => [update.notification_id, update]));
    const ids = [...latest.values()]
        .filter(update => update.succeeded && !hasNewerUpdate(update.notification_id))
        .map(update => update.notification_id);
    if (ids.length) {
        BellNotification.query()
            .findIn(ids)
            .filter(notification => notification.type === 'Deadline Reminder' && notification.read)
            .forEach(notification => BellNotification.delete(notification.notification_id));
    }
}

export default {
    name: 'AlertDrawer',

    components: { AlertCard, Badge, Drawer, InfiniteLoading, PrimeVueButton },
    emits: ['close-alert-drawer'],

    data: () => ({
        isLoaded: true,
        infiniteIsLoading: false,
        drawerPt,
        updatedNotifications: [],
        immediateUpdates: [],
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        open() {
            return this.$store.state.alertDrawer;
        },

        // Same gate as the Inbox nav links, so the footer never leads to the access-denied page.
        canOpenInbox() {
            return (
                this.$hasFeature('INBOX') &&
                canViewInbox(User.query().with(Gate.userRelationships()).find(this.user?.id))
            );
        },

        notificationsCounter() {
            return this.$store.state.entities.bell_notifications.counter;
        },

        notifications() {
            return BellNotification.query().orderBy('created_at', 'desc').get();
        },

        hasUnread() {
            return this.notifications.some(notification => !notification.read);
        },

        hasNextPage() {
            return (
                this.$store.state.entities.bell_notifications.page <
                this.$store.state.entities.bell_notifications.last_page
            );
        },

        currentPage: {
            get() {
                return this.$store.state.entities.bell_notifications.page;
            },

            set(value) {
                BellNotification.commit(state => (state.page = value));
            },
        },
    },

    async mounted() {
        this.isLoaded = false;
    },

    unmounted() {
        this.currentPage = 1;
    },

    methods: {
        /**
         * The header owns its Close button, so move focus there when the modal opens.
         */
        focusCloseButton() {
            this.$refs.closeButton?.$el?.focus();
        },

        updateNotification({ notification_id }, { immediate = false, pending } = {}) {
            if (immediate) {
                this.updatedNotifications = this.updatedNotifications.filter(id => id !== notification_id);
                this.immediateUpdates.push({
                    notification_id,
                    pending: Promise.resolve(pending).then(
                        () => true,
                        () => false
                    ),
                });
                return;
            }

            this.updatedNotifications = this.updatedNotifications.includes(notification_id)
                ? this.updatedNotifications.filter(id => id !== notification_id)
                : [...this.updatedNotifications, notification_id];
        },

        async closeAlertDrawer() {
            const { immediateUpdates } = this;
            this.immediateUpdates = [];
            const cleanup = removeReadReminders(immediateUpdates, id =>
                this.immediateUpdates.some(update => update.notification_id === id)
            );
            this.$emit('close-alert-drawer');
            this.$store.commit('SET_ALERT_DRAWER', { state: false });

            this.infiniteIsLoading = false;
            this.hasNextPage = true;

            if (this.updatedNotifications.length) {
                this.currentPage = 1;
                const notificationModels = BellNotification.query().findIn(this.updatedNotifications);
                await Promise.all(notificationModels.map(notification => notification.markAsRead()));
                notificationModels.forEach(notification => BellNotification.delete(notification.notification_id));
                await BellNotification.fetch(1);
                this.updatedNotifications = [];

                // Reload page 1 from the server now that the reads are committed.
                // fetch() sets `counter` from meta.total of the same query that
                // populates the drawer, so the badge is authoritative whether one,
                // many, or all notifications were marked read.
                await BellNotification.fetch();
            }

            await cleanup;
        },

        async fetchNotifications() {
            return BellNotification.api().get(`/api/notifications?page=${this.currentPage}`);
        },

        async handleLoadMore() {
            if (this.hasNextPage) {
                this.infiniteIsLoading = true;
                const page = this.currentPage + 1;
                this.currentPage = page;
                const response = await BellNotification.fetch(page);
                if (!response && this.currentPage === page) {
                    // Leave the page unloaded when it was discarded or failed so the loader retries it.
                    // Pause first so a persistent request failure does not retry in a tight loop.
                    this.currentPage = page - 1;
                    await new Promise(resolve => {
                        setTimeout(resolve, loadMoreRetryDelay);
                    });
                }
                this.infiniteIsLoading = false;
            }
        },

        async markAllAsRead() {
            const reminders = this.notifications.filter(notification => notification.type === 'Deadline Reminder');
            const ids = this.notifications
                .filter(notification => notification.type !== 'Deadline Reminder')
                .map(notification => notification.notification_id);
            const pending = reminders.map(notification => {
                const request = notification.markAsRead();
                this.updateNotification(notification, { immediate: true, pending: request });
                return request.catch(handleResponseError);
            });
            await BellNotification.update({
                where: notification => {
                    return ids.includes(notification.notification_id);
                },
                data: { read: true },
            });

            this.updatedNotifications = ids;
            if (pending.length) {
                await Promise.all(pending);
                await BellNotification.fetch();
            }
        },
    },
};
</script>
